import { Component, Inject } from '@angular/core';
import { StudentAttendanceService } from '../student-attendance.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastmessageService } from 'src/app/toastmessage.service';

export interface AttendanceFilterDialogData {
  attendanceDate?: Date;
  selectedStudyClassId?: any;
  selectedStudyClassName?: string;
  selectedBuildingId?: number | null;
  selectedBuildingName?: string;
  selectedSectionId?: any;
  selectedSectionName?: string;
}

export interface AttendanceFilterResult {
  attendanceDate: Date;
  selectedStudyClassId: any;
  selectedStudyClassName: string;
  selectedBuildingId: number | null;
  selectedBuildingName: string;
  selectedSectionId: any;
  selectedSectionName: string;
}
@Component({
  selector: 'app-student-attendance-filter',
  templateUrl: './student-attendance-filter.component.html',
  styleUrls: ['./student-attendance-filter.component.scss']
})
export class StudentAttendanceFilterComponent {
   attendanceDate: Date;
  selectedStudyClassId: any = null;
  selectedStudyClassName = '';
  selectedBuildingId: number | null = null;
  selectedBuildingName = '';
  selectedSectionId: any = null;
  selectedSectionName = '';

  studentclasslist: any[] = [];
  filteredStudyClassList: any[] = [];
  buildingList: any[] = [];
  filteredBuildingList: any[] = [];
  sectionList: any[] = [];
  filteredSectionList: any[] = [];
  noSectionFound = false;

  studyClassQuery = '';
  buildingQuery = '';
  sectionQuery = '';

  maxDate = new Date();
  loading = false;

  private readonly attendanceToastMs = 8000;

  constructor(
    private data: StudentAttendanceService,
    private dialogRef: MatDialogRef<StudentAttendanceFilterComponent>,
    private toast: ToastmessageService,
    @Inject(MAT_DIALOG_DATA) public initial: AttendanceFilterDialogData
  ) {
    this.attendanceDate = initial?.attendanceDate ? new Date(initial.attendanceDate) : new Date();
    this.selectedStudyClassId = initial?.selectedStudyClassId ?? null;
    this.selectedStudyClassName = initial?.selectedStudyClassName ?? '';
    this.selectedBuildingId = initial?.selectedBuildingId ?? null;
    this.selectedBuildingName = initial?.selectedBuildingName ?? '';
    this.selectedSectionId = initial?.selectedSectionId ?? null;
    this.selectedSectionName = initial?.selectedSectionName ?? '';
  }

  ngOnInit(): void {
    this.getclass();
    this.getBuildings();
    // When dialog opens with pre-selected class and building, load sections so dropdown is populated
    if (this.selectedStudyClassId && this.selectedBuildingId) {
      this.loadSections();
    }
  }

  getclass(): void {
    this.data.getstudentclass().subscribe({
      next: (res: any) => {
        this.studentclasslist = Array.isArray(res) ? res : [];
        this.filteredStudyClassList = [...this.studentclasslist];
      },
      error: () => {
        this.studentclasslist = [];
        this.filteredStudyClassList = [];
      }
    });
  }

  getBuildings(): void {
    this.data.getstundentbuilding().subscribe({
      next: (res: any) => {
        this.buildingList = Array.isArray(res) ? res : [];
        this.filteredBuildingList = [...this.buildingList];
      },
      error: () => {
        this.buildingList = [];
        this.filteredBuildingList = [];
      }
    });
  }

  loadSections(): void {
    if (!this.selectedStudyClassId || !this.selectedBuildingId) {
      this.sectionList = [];
      this.filteredSectionList = [];
      this.noSectionFound = false;
      return;
    }
    this.data.getstudentsection(this.selectedStudyClassId, this.selectedBuildingId).subscribe({
      next: (res: any) => {
        this.sectionList = Array.isArray(res) ? res : [];
        this.filteredSectionList = [...this.sectionList];
        this.noSectionFound = this.sectionList.length === 0;
        if (this.noSectionFound) {
          this.selectedSectionId = null;
          this.selectedSectionName = '';
          this.toast.showWarning('No Section', this.attendanceToastMs);
        }
      },
      error: () => {
        this.sectionList = [];
        this.filteredSectionList = [];
        this.noSectionFound = true;
        this.selectedSectionId = null;
        this.selectedSectionName = '';
        this.toast.showWarning('No Section', this.attendanceToastMs);
      }
    });
  }

  selectStudyClass(cls: any): void {
    this.selectedStudyClassId = cls.id;
    this.selectedStudyClassName = cls.studyClass;
    this.selectedBuildingId = null;
    this.selectedBuildingName = '';
    this.selectedSectionId = null;
    this.selectedSectionName = '';
    this.sectionList = [];
    this.filteredSectionList = [];
    this.noSectionFound = false;
  }

  selectBuilding(b: any): void {
    this.selectedBuildingId = b.id;
    this.selectedBuildingName = b.name;
    this.selectedSectionId = null;
    this.selectedSectionName = '';
    this.noSectionFound = false;
    this.loadSections();
  }

  selectSection(s: any): void {
    if (this.noSectionFound) {
      this.toast.showWarning('No Section', this.attendanceToastMs);
      return;
    }
    this.selectedSectionId = s.id;
    this.selectedSectionName = s.sectionName;
  }

  filterStudyClasses(): void {
    const q = this.studyClassQuery.toLowerCase();
    this.filteredStudyClassList = this.studentclasslist.filter(c =>
      (c.studyClass || '').toLowerCase().includes(q)
    );
  }

  filterBuildings(): void {
    const q = this.buildingQuery.toLowerCase();
    this.filteredBuildingList = this.buildingList.filter(b =>
      (b.name || '').toLowerCase().includes(q)
    );
  }

  filterSections(): void {
    const q = this.sectionQuery.toLowerCase();
    this.filteredSectionList = this.sectionList.filter((s: any) =>
      (s.sectionName || '').toLowerCase().includes(q)
    );
  }

  get isFilterComplete(): boolean {
    return !!this.attendanceDate &&
      this.selectedStudyClassId != null && this.selectedStudyClassId !== '' &&
      this.selectedBuildingId != null &&
      this.selectedSectionId != null && this.selectedSectionId !== '';
  }

  apply(): void {
    if (this.noSectionFound || !this.sectionList?.length) {
      this.toast.showWarning('No Section', this.attendanceToastMs);
      return;
    }
    if (!this.isFilterComplete) {
      this.toast.showWarning('Please select Date, Class, Building and Section', this.attendanceToastMs);
      return;
    }
    this.dialogRef.close({
      attendanceDate: this.attendanceDate,
      selectedStudyClassId: this.selectedStudyClassId,
      selectedStudyClassName: this.selectedStudyClassName,
      selectedBuildingId: this.selectedBuildingId,
      selectedBuildingName: this.selectedBuildingName,
      selectedSectionId: this.selectedSectionId,
      selectedSectionName: this.selectedSectionName
    } as AttendanceFilterResult);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
